"use client";

/**
 * The Orik application, as a client.
 *
 * The session is read once, from `/api/session`, and decides the page. A signed-in user gets the harness's app
 * (`AssistantApp`): the conversation, its list, the scheduled-task page and Settings are all surfaces of it, so
 * `/settings` and `/tasks` open those surfaces there. Everything else — sign-in, access denied, a credential form —
 * is Orik's own page (`auth-views.tsx`). Each is a lazy chunk, so a page loads only its own stylesheets.
 */
import { lazy, Suspense, useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { usePathname, useSearchParams } from "@/client/router";
import type { AuthView } from "@/client/auth-views";
import { INTEGRATIONS_SECTION, SCHEDULES_PANEL, type ShellLink } from "@/lib/agent/shell-links";
import { readSignedInHint, reloadForSession, writeSignedInHint } from "@/lib/signed-in-hint";

const AuthViews = lazy(() => import("@/client/auth-views"));
const AssistantApp = lazy(() => import("@/components/assistant-app").then(module => ({ default: module.AssistantApp })));

interface Session { authenticated: boolean; unavailable?: boolean; user?: string; hasIdentity?: boolean; transcription?: boolean }

/** Plain while no stylesheet of either kind has loaded. */
const LOADING_STYLE: CSSProperties = { display: "grid", height: "100dvh", placeItems: "center", font: "14px system-ui, sans-serif", opacity: 0.6 };

function Loading(): ReactNode {
  return <div role="status" style={LOADING_STYLE}>Loading…</div>;
}

/** The session, read once. */
function useSession(): { session?: Session; failed: boolean } {
  const [state, setState] = useState<{ session?: Session; failed: boolean }>({ failed: false });
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const response = await fetch("/api/session", { headers: { accept: "application/json" } });
        if (!response.ok) throw new Error(`HTTP ${String(response.status)}`);
        const session = await response.json() as Session;
        if (live) setState({ session, failed: false });
      } catch {
        if (live) setState({ failed: true });
      }
    })();
    return () => { live = false; };
  }, []);
  return state;
}

/** What a signed-in path asks the harness's shell to open, or undefined for a path the app does not serve. */
function shellLinkFor(pathname: string, params: URLSearchParams): ShellLink | undefined {
  if (pathname === "/" || pathname === "") return {};
  if (pathname === "/settings") {
    const error = params.get("error");
    return { settingsSection: INTEGRATIONS_SECTION, ...error === null ? {} : { integrationError: error } };
  }
  if (pathname === "/tasks") return { panel: SCHEDULES_PANEL };
  return undefined;
}

/** Whether a session answer opens the app itself (not sign-in, denial or a credential form) at this path. */
function opensApp(session: Session, pathname: string, params: URLSearchParams): boolean {
  return session.authenticated && session.hasIdentity === true && !pathname.startsWith("/credentials/") && shellLinkFor(pathname, params) !== undefined;
}

export function App(): ReactNode {
  const pathname = usePathname();
  const params = useSearchParams();
  const { session, failed } = useSession();
  // Read once: whether this page started the app before the session check answered.
  const [optimistic] = useState(() => {
    const user = readSignedInHint();
    return user !== undefined && !pathname.startsWith("/credentials/") && shellLinkFor(pathname, params) !== undefined ? user : undefined;
  });
  const settled = session !== undefined || failed;
  const confirmed = session !== undefined && opensApp(session, pathname, params);
  useEffect(() => {
    if (session === undefined) return;
    writeSignedInHint(confirmed ? session.user ?? "User" : undefined);
    // The app was started for a session that the check did not confirm: reload into the page it decides.
    if (optimistic !== undefined && !confirmed) reloadForSession();
  }, [session, confirmed, optimistic]);

  if (optimistic !== undefined && (!settled || confirmed || (failed && session === undefined))) {
    // A failed check keeps the optimistic app: its own requests carry the cookie and fail visibly if it is gone.
    return <Suspense fallback={<Loading />}>
      <AssistantApp user={session?.user ?? optimistic} link={shellLinkFor(pathname, params) ?? {}} initialThreadId={params.get("conversation") ?? undefined} transcriptionEnabled={Boolean(session?.transcription)} />
    </Suspense>;
  }
  if (optimistic !== undefined && session !== undefined && !confirmed) return <Loading />;

  let view: AuthView | undefined;
  let link: ShellLink | undefined;
  if (failed && session === undefined) view = { kind: "denied" };
  else if (session === undefined) return <Loading />;
  else if (!session.authenticated) {
    const requested = params.get("callbackURL") ?? "/";
    view = { kind: "sign-in", callbackURL: requested.startsWith("/") && !requested.startsWith("//") ? requested : "/", error: params.get("error") !== null };
  } else if (!session.hasIdentity) view = { kind: "denied" };
  else if (pathname.startsWith("/credentials/") && decodeURIComponent(pathname.slice("/credentials/".length)) !== "") {
    view = { kind: "credential", state: decodeURIComponent(pathname.slice("/credentials/".length)) };
  } else {
    link = shellLinkFor(pathname, params);
    if (link === undefined) view = { kind: "missing" };
  }

  return <Suspense fallback={<Loading />}>
    {view !== undefined
      ? <AuthViews view={view} />
      : <AssistantApp user={session?.user ?? "User"} link={link ?? {}} initialThreadId={params.get("conversation") ?? undefined} transcriptionEnabled={Boolean(session?.transcription)} />}
  </Suspense>;
}
